import { useCallback } from 'react'
import { useFormikContext } from 'formik'
import { ConditionModel } from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleForm/typings'
import { beRegEx, maxZipCodes, multiZipRegEx, zipRegEx } from '../constants'
import { useLocalisedValidations } from '@user/utils/useLocalisedValidations'
import { useTranslation } from 'react-i18next'

type Props = {
  setZipCodeValid: (value: boolean) => void
  formFieldName: string
}

export function useHandlers(props: Props) {
  const { setZipCodeValid, formFieldName } = props
  const { t } = useTranslation()
  const { validateMultiplePostalCode } = useLocalisedValidations()

  const {
    setFieldValue,
    values: { assignmentRuleConditionValues },
    setErrors,
    setFieldTouched,
    setStatus,
  } = useFormikContext<ConditionModel>()

  const removeZipCode = useCallback(
    (value: number) => {
      const codes = [...assignmentRuleConditionValues]
      codes.splice(value, 1)
      setFieldValue(formFieldName, codes)
      setStatus(null)
    },
    [formFieldName, setFieldValue, assignmentRuleConditionValues],
  )

  const extractFilteredZipCodes = useCallback(
    (s: string) => {
      return s
        ? s
            .replace(beRegEx, '')
            .replace(multiZipRegEx, ',')
            .split(',')
            .filter((v) => !assignmentRuleConditionValues.includes(v))
        : []
    },
    [assignmentRuleConditionValues],
  )

  const setZipCodes = useCallback(
    (s) => {
      if (!s) {
        return
      }

      setStatus(null)
      const filteredZipCodes = extractFilteredZipCodes(s)

      const matchedZips = validateMultiplePostalCode(filteredZipCodes, true)

      const matches = matchedZips.reduce((r: string[], e) => {
        if (r.length < maxZipCodes - assignmentRuleConditionValues.length) {
          r.push(e)
        }
        return r
      }, [])

      setFieldValue(formFieldName, [
        ...assignmentRuleConditionValues,
        ...matches,
      ])
    },
    [
      setStatus,
      formFieldName,
      setFieldValue,
      assignmentRuleConditionValues,
      extractFilteredZipCodes,
      validateMultiplePostalCode,
    ],
  )

  const handZipCodeInput = useCallback(
    ({ target }) => {
      setZipCodes(target?.value)
    },
    [setZipCodes],
  )

  const validate = useCallback(
    (s: string) => {
      if (s) {
        setFieldTouched(formFieldName, true)

        const filteredZipCodes = extractFilteredZipCodes(s)

        const unMatchedZips = validateMultiplePostalCode(filteredZipCodes)

        if (unMatchedZips?.length > 0) {
          setZipCodeValid(false)
          setErrors({
            assignmentRuleConditionValues: `${t(
              'invalidCodes',
            )}: ${unMatchedZips.join(', ')}`,
          })
        } else {
          setZipCodeValid(true)
          setErrors({})
          const maxZipMatches =
            maxZipCodes - assignmentRuleConditionValues.length

          if (filteredZipCodes.length > maxZipMatches) {
            setStatus({
              message: `Only ${maxZipCodes} ${t(
                'postalCode',
              )}s are allowed.  The following ${t(
                'postalCode',
              )}(s) will not be added: ${filteredZipCodes
                .slice(maxZipMatches)
                .join(', ')}`,
            })
          }
        }
      } else {
        setZipCodeValid(true)
        setErrors({})
        setStatus(null)
      }
    },
    [
      formFieldName,
      setErrors,
      setFieldTouched,
      setZipCodeValid,
      assignmentRuleConditionValues,
      zipRegEx,
      extractFilteredZipCodes,
      validateMultiplePostalCode,
    ],
  )

  const validateZipCodes = useCallback(
    ({ target }) => {
      validate(target?.value)
    },
    [validate],
  )

  const handleZipBlur = useCallback(
    ({ target }) => {
      setZipCodes(target?.value)
    },
    [setZipCodes],
  )

  const clearFields = useCallback(() => {
    setFieldValue(formFieldName, [])
    setErrors({})
    setStatus(null)
  }, [formFieldName, setFieldValue, setErrors])

  const handleChange = useCallback(
    (_, nv) => {
      if (!nv || nv.length === 0) {
        clearFields()
      }
    },
    [clearFields],
  )

  const handleOnClick = useCallback(
    ({ target }) => {
      if (target.value?.trim() && !assignmentRuleConditionValues?.length) {
        clearFields()
      }
    },
    [clearFields, assignmentRuleConditionValues],
  )

  return {
    removeZipCode,
    handleZipBlur,
    validateZipCodes,
    handZipCodeInput,
    handleChange,
    handleOnClick,
  }
}
